import React, { useState } from 'react';
import { Checkbox, Input, Popover } from 'antd';

import type { FilterState } from './resultsView';
import type { SiteGroup } from './siteSelection';
import styles from './styles.module.css';

interface SiteFiltersProps {
  groups: SiteGroup[];
  /** What each chip shows checked: the picks, or the sites the search started with. */
  value: FilterState;
  /** Called on Apply or Reset with the picks of every group. */
  onChange: (next: FilterState) => void;
}

const SHOWN_CODES = 3;

const summaryOf = (group: SiteGroup, selected: string[]): string => {
  if (selected.length === group.options.length) return 'All';
  const shown = selected.slice(0, SHOWN_CODES).join(', ');
  const more = selected.length - SHOWN_CODES;
  return more > 0 ? `${shown} +${more}` : shown;
};

const isDefault = (group: SiteGroup, selected: string[]): boolean =>
  selected.length === group.defaults.length &&
  group.defaults.every((code) => selected.includes(code));

interface SiteChipProps {
  group: SiteGroup;
  selected: string[];
  onApply: (next: string[]) => void;
}

/** One origin or destination airport/port as a chip; its list stages the picks until Apply. */
const SiteChip: React.FC<SiteChipProps> = ({ group, selected, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selected);
  const [query, setQuery] = useState('');

  const shown = group.options.filter((option) =>
    option.label.toLowerCase().includes(query.trim().toLowerCase())
  );
  const allShown =
    shown.length > 0 && shown.every((option) => draft.includes(option.value));

  const toggle = (value: string, checked: boolean): void =>
    setDraft((current) =>
      checked ? [...current, value] : current.filter((v) => v !== value)
    );
  const toggleShown = (): void =>
    setDraft((current) =>
      allShown
        ? current.filter((v) => !shown.some((o) => o.value === v))
        : [...new Set([...current, ...shown.map((o) => o.value)])]
    );
  const close = (): void => {
    setOpen(false);
    setQuery('');
  };

  const content = (
    <div
      className={styles.siteChipPopover}
      role='dialog'
      aria-label={group.label}
    >
      <div className={styles.siteChipTitle}>{group.label}</div>
      <Input
        size='small'
        allowClear
        placeholder='Search'
        aria-label={`Search ${group.label}`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <Checkbox checked={allShown} onChange={toggleShown}>
        Select all
      </Checkbox>
      <div className={styles.siteChipList}>
        {shown.map((option) => (
          <Checkbox
            key={option.value}
            checked={draft.includes(option.value)}
            onChange={(e) => toggle(option.value, e.target.checked)}
          >
            {option.label}
          </Checkbox>
        ))}
      </div>
      <div className={styles.siteChipActions}>
        <button
          type='button'
          className={styles.seeMore}
          onClick={() => {
            onApply(group.defaults);
            close();
          }}
        >
          Reset
        </button>
        <button
          type='button'
          className={styles.popoverApply}
          disabled={draft.length === 0}
          onClick={() => {
            onApply(draft);
            close();
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setDraft(selected);
        setOpen(next);
        if (!next) setQuery('');
      }}
      trigger='click'
      placement='bottomLeft'
      arrow={false}
      content={content}
    >
      <button
        type='button'
        className={`${styles.siteChip} ${
          isDefault(group, selected) ? '' : styles.pillActive
        }`}
        aria-expanded={open}
        aria-label={`${group.label}: ${summaryOf(group, selected)}`}
      >
        <span className={styles.siteChipLabel}>{group.label}</span>
        <span className={styles.siteChipValue}>
          {summaryOf(group, selected)}
        </span>
      </button>
    </Popover>
  );
};

/** The origin/destination airport and port filters, one chip each. */
const SiteFilters: React.FC<SiteFiltersProps> = ({
  groups,
  value,
  onChange,
}) => {
  if (!groups.length) return null;
  return (
    <>
      {groups.map((group) => (
        <SiteChip
          key={group.key}
          group={group}
          selected={value[group.key] ?? group.defaults}
          onApply={(next) => onChange({ ...value, [group.key]: next })}
        />
      ))}
    </>
  );
};

export default SiteFilters;
